import { useEffect } from "react";
import { DEFAULT_IMAGE, type SeoConfig, canonicalUrl, pageJsonLd } from "@/lib/seo";

function upsertMeta(selector: string, create: () => HTMLMetaElement, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertLink(selector: string, create: () => HTMLLinkElement, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

export function RouteSeo(config: SeoConfig) {
  useEffect(() => {
    const url = canonicalUrl(config.path);
    const image = config.image || DEFAULT_IMAGE;

    document.title = config.title;

    upsertMeta('meta[name="description"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      return meta;
    }, config.description);

    upsertLink('link[rel="canonical"]', () => {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      return link;
    }, url);

    const metaPairs = [
      ["property", "og:title", config.title],
      ["property", "og:description", config.description],
      ["property", "og:url", url],
      ["property", "og:image", image],
      ["property", "og:type", config.type === "article" ? "article" : "website"],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", config.title],
      ["name", "twitter:description", config.description],
      ["name", "twitter:image", image],
    ] as const;

    metaPairs.forEach(([attr, key, value]) => {
      upsertMeta(`meta[${attr}="${key}"]`, () => {
        const meta = document.createElement("meta");
        meta.setAttribute(attr, key);
        return meta;
      }, value);
    });

    const scriptId = "route-json-ld";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(pageJsonLd(config));
  }, [config.title, config.description, config.path, config.image, config.type, config.jsonLd]);

  return null;
}
